import { AuthenticationApi } from '@ais-ui/api-spec/oceanio';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createApiConfiguration } from './apiConfiguration';
import { useOceanioConfig } from './config';
import { AuthContext, type AuthDialog } from './LoginContext';
import { LoginCard } from './LoginCard';
import { RegistrationCard } from './RegistrationCard';
import {
  clearStoredSession,
  readStoredSession,
  storeSession,
  type Session,
} from './session';
import { SuccessfulRegistrationCard } from './SuccessfulRegistrationCard';

export { useApiConfiguration, useLogin } from './LoginContext';

// setTimeout overflows above ~24.8 days, so longer waits are done in steps.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

interface LoginProviderProps {
  children: ReactNode;
  initialSession?: Session;
}

export const LoginProvider = ({
  children,
  initialSession,
}: LoginProviderProps) => {
  const config = useOceanioConfig();
  const [session, setSession] = useState<Session | undefined>(
    () => initialSession ?? readStoredSession(),
  );
  const [openDialog, setOpenDialog] = useState<AuthDialog | null>(null);

  // Late 401s and timers belong to the token that started them; only the
  // current token may end the session.
  const currentToken = useRef(session?.token);

  const expireSession = useCallback((token: string | undefined) => {
    if (token == null || token !== currentToken.current) return;
    currentToken.current = undefined;
    clearStoredSession();
    setSession(undefined);
    setOpenDialog('login');
  }, []);

  const apiConfiguration = useMemo(
    () =>
      createApiConfiguration({
        config,
        token: session?.token,
        onUnauthorized: () => expireSession(session?.token),
      }),
    [config, session?.token, expireSession],
  );

  useSessionExpiry(session, expireSession);

  const value = useMemo(
    () => ({
      session,
      apiConfiguration,
      login: (newSession: Session) => {
        currentToken.current = newSession.token;
        storeSession(newSession);
        setSession(newSession);
        setOpenDialog(null);
      },
      logout: () => {
        // Best effort: the local session ends even if the server call fails.
        // An expired token answers 401; that must not reopen the login dialog.
        const logoutConfiguration = createApiConfiguration({
          config,
          token: session?.token,
          onUnauthorized: () => undefined,
        });
        new AuthenticationApi(logoutConfiguration)
          .authenticationLogoutDelete()
          .catch(() => undefined);
        currentToken.current = undefined;
        clearStoredSession();
        setSession(undefined);
      },
      openDialog,
      showDialog: setOpenDialog,
      closeDialog: () => setOpenDialog(null),
    }),
    [config, session, apiConfiguration, openDialog],
  );

  return (
    <AuthContext.Provider value={value}>
      <LoginCard />
      <RegistrationCard />
      <SuccessfulRegistrationCard />
      {children}
    </AuthContext.Provider>
  );
};

/** Ask the user to log in again once the token runs out. */
function useSessionExpiry(
  session: Session | undefined,
  onExpire: (token: string) => void,
) {
  useEffect(() => {
    if (!session) {
      return;
    }
    let timeout: ReturnType<typeof setTimeout>;
    const waitForExpiry = () => {
      const remainingMs = session.expiresAt - Date.now();
      if (remainingMs <= 0) {
        onExpire(session.token);
        return;
      }
      timeout = setTimeout(
        waitForExpiry,
        Math.min(remainingMs, MAX_TIMEOUT_MS),
      );
    };
    waitForExpiry();
    return () => clearTimeout(timeout);
  }, [session, onExpire]);
}
